import BreadCrumbs from 'components/breadcrumbs'
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardText,
  Col,
  Collapse,
  Row,
} from 'reactstrap'
import { useDispatch, useSelector } from 'react-redux'
import { setFetchOption, toggleSidebarEvent } from '../eventSlice'
import CustomButton from 'components/Buttons/CustomButton'
import { useLocation, useNavigate } from 'react-router-dom'
import { URL_EVENT, URL_EVENT_FORM, URL_EVENT_PARTICIPANT, URL_FARMERS } from 'utils/pathUrl'
import { useFetchEventQuery, useFetchUtilsEventQuery } from '../eventApiSlice'
import { formatDateOnlyPMB, formatTimeOnlyPMB } from 'utils/formatDate'
import { ResponsiveActionButton } from 'components/Buttons'
import SidebarDetailEvent from '../SidebarDetailEvent'
import { useEffect, useRef, useState, useCallback } from 'react'
import { Can } from 'context/Acl'
import { BUSINESS_EVENT, MANAGE } from 'utils/subjectActions'
import PMBSearchInputGroup from 'components/PMBSearchInputGroup'
import { useDebounce } from 'helpers'
import {
  Calendar,
  ChevronDown,
  ChevronUp,
  Filter,
  MapPin,
  Plus,
  User,
  X,
} from 'react-feather'
import LoadingAnimation from 'components/LoadingAnimation'
import PMBModalImagePreview from 'components/PMBModalImagePreview'
import PMBSelect from 'components/PMBSelect'
import ImageCrash from 'assets/images/404.png'
import { P } from 'components'

const getDynamicBadgeColor = (list, value) => {
  const lowerValue = value?.toLowerCase?.() || ''
  const index = list.findIndex(
    (item) => item.label.toLowerCase() === lowerValue,
  )

  if (index === -1) return 'bg-light text-secondary'

  const colors = [
    'bg-primary-50 text-primary',
    'bg-success-50 text-success',
    'bg-warning-50 text-warning-700',
    'bg-primary-50 text-info',
    'bg-danger-50 text-danger-700',
  ]

  return colors[index % colors.length]
}

const TimelineEvent = ({
  events,
  level = 0,
  onSelectRow,
  onViewParticipant,
  onEdit,
  onOpenImage,
  resUtils,
  userId,
}) => {
  const eventTypeList = resUtils?.data?.eventType || []
  const categoryList = resUtils?.data?.eventCategory || []
  const statusList = resUtils?.data?.eventParticipantStatus || []

  if (!events || events.length === 0) return null

  return (
    <div className={`ms-${level * 4}`}>
      {events.map((event) => {
        const eventTypeColor = getDynamicBadgeColor(
          eventTypeList,
          event.eventTypeName,
        )
        const categoryColor = getDynamicBadgeColor(
          categoryList,
          event.categoryName,
        )
        const statusText = event.status === 2 ? 'Terpublikasi' : 'Draf'
        const statusColor = getDynamicBadgeColor(statusList, statusText)

        let attachments = []
        try {
          attachments = Array.isArray(event?.attachments)
            ? event?.attachments
            : JSON.parse(event?.attachments || '[]')
        } catch {
          attachments = []
        }

        const imageFiles = attachments?.filter((a) =>
          /\.(jpg|jpeg|png|gif|webp)$/i.test(a),
        )
        const pdfFiles = attachments?.filter((a) => /\.pdf$/i.test(a))

        const primaryFile = imageFiles[0] || pdfFiles[0] || null
        const isPdf = /\.pdf$/i.test(primaryFile)

        return (
          <div key={event.id} className="mb-4">
            <Card
              className={`shadow-sm border ${level > 0 ? 'ms-3 border-info-subtle' : ''
                }`}
              style={{
                borderLeft: level > 0 ? '4px solid #3b82f6' : undefined,
                borderRadius: '12px',
                backgroundColor: level > 0 ? '#f8fafc' : '#ffffff',
              }}
              data-testid={`event-card-${event.id}`}
            >
              <CardBody className="p-3">
                <div className="d-flex align-items-start justify-content-between flex-md-nowrap flex-wrap gap-2">
                  {/* Date section */}
                  <div
                    className="text-center me-3"
                    style={{ minWidth: '65px' }}
                  >
                    <div className="fw-bold fs-5 lh-1">
                      {
                        formatDateOnlyPMB(
                          event?.eventDate ?? event?.startDate,
                        ).split(' ')[0]
                      }
                    </div>
                    <div className="text-muted small lh-1">
                      {
                        formatDateOnlyPMB(
                          event?.eventDate ?? event?.startDate,
                        ).split(' ')[1]
                      }{' '}
                      {
                        formatDateOnlyPMB(
                          event?.eventDate ?? event?.startDate,
                        ).split(' ')[2]
                      }
                    </div>
                    <div className="text-muted small lh-1">
                      {formatTimeOnlyPMB(event?.eventDate ?? event?.startDate)}
                    </div>
                  </div>

                  {/* Event details */}
                  <div className="flex-grow-1">
                    <div className="d-flex justify-content-between align-items-start flex-wrap mb-1">
                      <div className="d-flex align-items-center flex-wrap gap-2">
                        <h6 className="fw-bold mb-0">{event.title}</h6>

                        <span
                          className={`badge rounded-pill px-2 ${eventTypeColor}`}
                        >
                          {event.eventTypeName || '-'}
                        </span>
                        <span
                          className={`badge rounded-pill px-2 ${categoryColor}`}
                        >
                          {event.categoryName || '-'}
                        </span>
                      </div>

                      <div className="d-flex flex-wrap justify-content-end gap-2">
                        <ResponsiveActionButton
                          iconName="eye"
                          label="Detail"
                          onClick={() => onSelectRow(event)}
                          testId={`event-card-detail-${event.id}`}
                        />
                        {!userId && (
                          <>
                            <Can I={MANAGE} this={BUSINESS_EVENT}>
                              <ResponsiveActionButton
                                iconName="profile-outline"
                                label="Peserta"
                                onClick={() => onViewParticipant(event)}
                                testId={`event-card-participant-${event.id}`}
                              />
                            </Can>

                            <Can I={MANAGE} this={BUSINESS_EVENT}>
                              <ResponsiveActionButton
                                iconName="edit"
                                label="Ubah"
                                onClick={() => onEdit(event)}
                                testId={`event-card-edit-${event.id}`}
                              />
                            </Can>
                          </>
                        )}
                      </div>
                    </div>

                    {event?.description && !userId && (
                      <div className="mt-1">{event.description}</div>
                    )}

                    {event?.note && userId && (
                      <div className="mt-1">{event.note}</div>
                    )}

                    {event?.institutionName && (
                      <div className="mt-1">Oleh {event?.institutionName}</div>
                    )}
                    {event?.location && (
                      <div className="mt-1">
                        <MapPin size={13} /> {event.location}
                      </div>
                    )}

                    {attachments?.length > 0 && (
                      <div
                        style={{
                          position: 'relative',
                          width: '70px',
                          height: '70px',
                          overflow: 'hidden',
                          borderRadius: '8px',
                          marginTop: '8px',
                          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                          border: '1px solid #f0f0f0',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          backgroundColor: '#f9f9f9',
                          cursor: 'pointer',
                        }}
                        onClick={() => onOpenImage(event)}
                      >
                        {isPdf ? (
                          <>
                            <div className="text-center">
                              <i
                                className="fa fa-file-pdf-o text-danger"
                                style={{ fontSize: 28 }}
                              ></i>
                              <div style={{ fontSize: '10px' }}>PDF</div>
                            </div>
                          </>
                        ) : (
                          <img
                            src={primaryFile}
                            onError={(e) => {
                              e.currentTarget.onerror = null
                              e.currentTarget.src = ImageCrash
                            }}
                            alt="event-attachment"
                            loading="lazy"
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              transition: 'transform 0.2s ease-in-out',
                            }}
                            onMouseOver={(e) =>
                              (e.currentTarget.style.transform = 'scale(1.05)')
                            }
                            onMouseOut={(e) =>
                              (e.currentTarget.style.transform = 'scale(1)')
                            }
                          />
                        )}

                        {attachments?.length - 1 > 0 && (
                          <div
                            style={{
                              position: 'absolute',
                              top: '4px',
                              right: '4px',
                              width: '20px',
                              height: '20px',
                              borderRadius: '50%',
                              backgroundColor: 'rgba(0, 0, 0, 0.7)',
                              color: 'white',
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              fontSize: '10px',
                              fontWeight: 'bold',
                              border: '1px solid #fff',
                            }}
                          >
                            +{attachments?.length - 1}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Recursive child */}
                {event.child && event.child.length > 0 && (
                  <div className="mt-3 border-start ps-3">
                    <TimelineEvent
                      events={event.child}
                      level={level + 1}
                      onSelectRow={onSelectRow}
                      onViewParticipant={onViewParticipant}
                      onEdit={onEdit}
                      onOpenImage={onOpenImage}
                      resUtils={resUtils}
                      userId={userId}
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </div>
        )
      })}
    </div>
  )
}

const ListEvent = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const dataState = location.state
  const queryParams = new URLSearchParams(location.search)
  const userId = queryParams.get('userId')

  console.log(dataState)

  const { tag, isOpenSidebar } = useSelector((s) => s.event)
  const [selectedRow, setSelectedRow] = useState(null)
  const [searchInput, setSearchInput] = useState('')
  const [events, setEvents] = useState([])
  const [modalOpenImage, setModalOpenImage] = useState(false)
  const [isFilterOpen, setIsFilterOpen] = useState(false)

  const res = useFetchEventQuery(
    {
      ...tag,
      userId: userId || undefined,
    },
    { skip: !tag },
  )
  const resUtils = useFetchUtilsEventQuery()

  useEffect(() => {
    handleClearFilters()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  useEffect(() => {
    if (!res?.data?.items) return

    setEvents((prev) => {
      const newItems = res.data.items
      const isSame =
        prev.length === newItems.length &&
        prev.every((p, i) => p.id === newItems[i].id)

      if (isSame) return prev

      if (tag?.page === 0) {
        return newItems
      } else {
        const merged = [...prev]
        newItems.forEach((item) => {
          if (!merged.some((x) => x.id === item.id)) merged.push(item)
        })
        return merged
      }
    })
  }, [res?.data?.items, tag?.page])

  const totalItems = res?.data?.totalItems ?? 0

  const getActiveFiltersCount = () => {
    let count = 0
    if (tag.search) count++
    if (tag.objectTypeId) count++
    if (tag.eventTypeId) count++
    if (tag.categoryId) count++
    return count
  }

  const handleClearFilters = useCallback(() => {
    dispatch(
      setFetchOption({
        search: undefined,
        objectTypeId: undefined,
        eventTypeId: undefined,
        categoryId: undefined,
        page: 0,
      }),
    )
    setSearchInput('')
    setEvents([])
  }, [dispatch])

  const handleFetchNextPage = useCallback(() => {
    if (!res.isFetching && events.length < totalItems) {
      dispatch(setFetchOption({ page: (tag?.page ?? 0) + 1 }))
    }
  }, [res.isFetching, events.length, totalItems, tag?.page, dispatch])

  const listRef = useRef(null)
  useEffect(() => {
    const div = listRef.current
    if (!div) return

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = div
      if (scrollTop + clientHeight >= scrollHeight - 50) {
        handleFetchNextPage()
      }
    }

    div.addEventListener('scroll', handleScroll)
    return () => div.removeEventListener('scroll', handleScroll)
  }, [handleFetchNextPage])

  const debouncedSearch = useDebounce((value) => {
    dispatch(setFetchOption({ page: 0, search: value }))
  }, 600)

  const onHandleSearchChange = (value) => {
    setSearchInput(value ?? '')
    debouncedSearch(value ?? '')
  }

  return (
    <div className="w-100 px-3 pb-3">
      <div className="d-flex align-items-start gap-2 pt-3">
        <BreadCrumbs breadCrumbActive={'Event'}
          breadCrumbParent={dataState?.fullName ? 'Daftar Anggota' : ''}
          breadCrumbParentLink={dataState?.fullName ? URL_FARMERS : ''}
          breadCrumbParent2={dataState?.fullName ? dataState.fullName : ''}
          breadCrumbParent2Link={dataState?.fullName ? `${URL_FARMERS}/${userId}` : ''}
        />
      </div>

      <PMBModalImagePreview
        isOpen={modalOpenImage}
        toggle={() => setModalOpenImage(!modalOpenImage)}
        images={
          Array.isArray(selectedRow?.attachments)
            ? selectedRow?.attachments
            : JSON.parse(selectedRow?.attachments || '[]')
        }
        isDownload
      />

      <SidebarDetailEvent
        toggle={() => dispatch(toggleSidebarEvent())}
        isOpen={isOpenSidebar}
        detailRow={selectedRow}
        userId={userId}
      />

      <Card className="border-0 shadow-sm">
        <CardBody>
          <div className="w-100">
            <div className="d-flex flex-column flex-md-row justify-content-between w-100 mb-2">
              <CardText tag="h5">
                {`Timeline Event ${dataState ? (dataState?.name ?? dataState?.fullName) : ''}`}
              </CardText>
              <div className="d-flex gap-2">
                <div className="d-flex align-items-center gap-2 flex-md-grow-1">
                  {getActiveFiltersCount() > 0 && (
                    <div className="flex-shrink-0">
                      <CustomButton
                        onClick={handleClearFilters}
                        color="danger"
                        className="d-flex align-items-center justify-content-center"
                        testId="event-filter-clear-button"
                        style={{
                          width: '40px',
                          height: '40px',
                          padding: 0,
                          minWidth: '40px',
                        }}
                      >
                        <X size={16} />
                      </CustomButton>
                    </div>
                  )}
                  <div>
                    <CustomButton
                      onClick={() => setIsFilterOpen(!isFilterOpen)}
                      color="primary"
                      outline
                      className="w-100 position-relative"
                      testId="event-filter-toggle-button"
                    >
                      <div className="d-flex align-items-center gap-2 justify-content-center">
                        <Filter size={16} />
                        <P>Filter</P>
                        {getActiveFiltersCount() > 0 && (
                          <Badge
                            color="danger"
                            pill
                            style={{
                              position: 'absolute',
                              top: '-5px',
                              right: '-5px',
                              fontSize: '10px',
                            }}
                          >
                            {getActiveFiltersCount()}
                          </Badge>
                        )}
                        {isFilterOpen ? (
                          <ChevronUp size={16} />
                        ) : (
                          <ChevronDown size={16} />
                        )}
                      </div>
                    </CustomButton>
                  </div>
                </div>
                {!userId && (
                  <Can I={MANAGE} this={BUSINESS_EVENT}>
                    <CustomButton
                      color="primary"
                      outline
                      className="d-flex align-items-center gap-2"
                      onClick={() => navigate(URL_EVENT_FORM)}
                      testId="event-add-button"
                    >
                      <Plus size={16} />
                      Tambah Event
                    </CustomButton>
                  </Can>
                )}
              </div>
            </div>

            <Collapse isOpen={isFilterOpen}>
              <div
                className="border rounded p-3 bg-light mb-3 filter-collapse"
                style={{
                  width: '100%',
                  transition: 'all 0.3s ease-in-out',
                  opacity: isFilterOpen ? 1 : 0,
                  transform: isFilterOpen
                    ? 'translateY(0)'
                    : 'translateY(-10px)',
                }}
              >
                <Row className="g-2">
                  <Col xs={12} md={4}>
                    <p className="mb-2">Cari</p>
                    <PMBSearchInputGroup
                      value={searchInput}
                      placeholder="Cari..."
                      onChange={(e) => onHandleSearchChange(e.target.value)}
                      maxWidth="100%"
                      testId="event-filter-search-input"
                    />
                  </Col>
                  <Col xs={12} md={4}>
                    <p className="mb-2">Tipe Objek</p>
                    <PMBSelect
                      style={{ width: '100%' }}
                      placeholder="Semua Tipe Objek"
                      options={resUtils?.data?.objectType}
                      onChange={(e) =>
                        dispatch(
                          setFetchOption({
                            objectTypeId: e?.value,
                          }),
                        )
                      }
                      value={
                        resUtils?.data?.objectType?.find(
                          (e) => e.value === tag.objectTypeId,
                        ) ?? null
                      }
                      menuPortalTarget={
                        typeof window !== 'undefined' ? document.body : null
                      }
                      isClearable
                      testId="event-filter-object-type"
                    />
                  </Col>

                  <Col xs={12} md={4}>
                    <p className="mb-2">Tipe Event</p>
                    <PMBSelect
                      style={{ width: '100%' }}
                      placeholder="Semua Tipe Event"
                      options={resUtils?.data?.eventType}
                      onChange={(e) =>
                        dispatch(
                          setFetchOption({
                            eventTypeId: e?.value,
                          }),
                        )
                      }
                      value={
                        resUtils?.data?.eventType?.find(
                          (e) => e.value === tag.eventTypeId,
                        ) ?? null
                      }
                      menuPortalTarget={
                        typeof window !== 'undefined' ? document.body : null
                      }
                      isClearable
                      testId="event-filter-event-type"
                    />
                  </Col>

                  <Col xs={12} md={4}>
                    <p className="mb-2">Kategori Event</p>
                    <PMBSelect
                      style={{ width: '100%' }}
                      placeholder="Semua Kategori Event"
                      options={resUtils?.data?.eventCategory}
                      onChange={(e) =>
                        dispatch(
                          setFetchOption({
                            categoryId: e?.value,
                          }),
                        )
                      }
                      value={
                        resUtils?.data?.eventCategory?.find(
                          (e) => e.value === tag.categoryId,
                        ) ?? null
                      }
                      menuPortalTarget={
                        typeof window !== 'undefined' ? document.body : null
                      }
                      isClearable
                      testId="event-filter-category"
                    />
                  </Col>
                </Row>
              </div>
            </Collapse>
          </div>

          {/* Timeline Cards with Scroll */}
          <div
            ref={listRef}
            style={{
              maxHeight: '70vh',
              overflowY: 'auto',
              paddingRight: '6px',
            }}
          >
            <TimelineEvent
              events={res?.data?.items}
              onSelectRow={(row) => {
                setSelectedRow(row)
                dispatch(toggleSidebarEvent())
              }}
              onViewParticipant={(row) =>
                navigate(`${URL_EVENT_PARTICIPANT}?id=${row?.id}`, {
                  state: row,
                })
              }
              onEdit={(row) => navigate(URL_EVENT_FORM, { state: row })}
              onOpenImage={(event) => {
                setSelectedRow(event)
                setModalOpenImage(true)
              }}
              resUtils={resUtils}
              userId={userId}
            />

            {res.isFetching && (
              <div className="text-center py-3 text-muted small">
                <LoadingAnimation />
              </div>
            )}

            {!res.isFetching && events.length === 0 && (
              <div className="text-center py-4 text-muted">
                Tidak ada data event
              </div>
            )}
          </div>
        </CardBody>
      </Card>
    </div>
  )
}

export default ListEvent
